import { CheckCircleOutlined, ClockCircleOutlined, SearchOutlined, TruckOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Input, List, message, Space, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDeliveryByTrackingNumber, useUserDeliveries } from '../hooks/useApi';
import { useI18n } from '../i18n';
import { Delivery } from '../services/api';

const { Search } = Input;
const { Title, Text } = Typography;

const LogisticsTracking: React.FC = () => {
  const { t } = useI18n();

  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  // 配送状态映射
  const statusMap: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
    PENDING: { color: 'orange', text: '待發貨', icon: <ClockCircleOutlined /> },
    PICKED_UP: { color: 'blue', text: '已取件', icon: <TruckOutlined /> },
    IN_TRANSIT: { color: 'cyan', text: '運輸中', icon: <TruckOutlined /> },
    OUT_FOR_DELIVERY: { color: 'purple', text: '派送中', icon: <TruckOutlined /> },
    DELIVERED: { color: 'green', text: '已送達', icon: <CheckCircleOutlined /> },
    FAILED: { color: 'red', text: '派送失敗', icon: <ClockCircleOutlined /> },
  };

  // 模拟用户ID
  const userId = 1;

  // 使用SWR获取配送数据
  const {
    deliveries: allDeliveries,
    isLoading: deliveriesLoading,
    error: deliveriesError
  } = useUserDeliveries(userId);

  const {
    delivery: trackedDelivery,
    isLoading: trackingLoading,
    error: trackingError
  } = useDeliveryByTrackingNumber(trackingNumber);

  // 根据搜索关键词过滤配送信息
  const getFilteredDeliveries = () => {
    if (!searchKeyword.trim()) {
      return allDeliveries;
    }

    return allDeliveries.filter(delivery =>
      delivery.trackingNumber.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      delivery.carrier.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  };

  const deliveries = getFilteredDeliveries();
  const loading = deliveriesLoading || trackingLoading;

  // 处理错误
  useEffect(() => {
    if (deliveriesError) {
      message.error(t('messages.logistics.loadFailed'));
      console.error('Error loading deliveries:', deliveriesError);
    }
  }, [deliveriesError, t]);

  useEffect(() => {
    if (trackingError) {
      message.error(t('messages.logistics.queryFailed'));
      console.error('Error tracking delivery:', trackingError);
    }
  }, [trackingError, t]);

  // 根据跟踪号查询
  const handleTrackByNumber = () => {
    if (!trackingNumber.trim()) {
      message.warning(t('messages.logistics.enterTrackingNumber'));
      return;
    }
    setSelectedDelivery(trackedDelivery || null);
  };

  // 搜索配送信息
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
  };

  // 格式化时间
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="logistics-tracking" style={{ padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          <TruckOutlined /> {t('logistics.title')}
        </Title>

        {/* 跟踪号查询 */}
        <Card style={{ marginBottom: '24px' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder={t('logistics.trackByNumber.placeholder')}
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              onPressEnter={handleTrackByNumber}
            />
            <Button className="btn-primary-unified" icon={<SearchOutlined />} onClick={handleTrackByNumber}>
              {t('logistics.trackByNumber.button')}
            </Button>
          </Space.Compact>
        </Card>

        {/* 配送列表 */}
        <Card title={t('logistics.list.title')}>
          <Space style={{ marginBottom: '16px' }}>
            <Search
              placeholder={t('logistics.list.searchPlaceholder')}
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
            <Button onClick={() => window.location.reload()}>{t('logistics.list.refresh')}</Button>
          </Space>

          <List
            loading={loading}
            dataSource={deliveries}
            renderItem={(delivery) => (
              <List.Item
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedDelivery(delivery)}
              >
                <List.Item.Meta
                  avatar={<TruckOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                  title={
                    <Space>
                      <Text strong>{t('logistics.list.trackingNumberLabel')}: {delivery.trackingNumber}</Text>
                      <Tag color={statusMap[delivery.status]?.color} icon={statusMap[delivery.status]?.icon}>
                        {statusMap[delivery.status]?.text}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">{t('logistics.list.carrierLabel')}: {delivery.carrier}</Text>
                      <br />
                      <Text type="secondary">{t('logistics.list.createdAtLabel')}: {formatTime(delivery.createdAt)}</Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        {/* 配送詳情模態框 */}
        {selectedDelivery && (
          <Card title={t('logistics.detail.title')} style={{ marginTop: '16px' }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('logistics.detail.labels.trackingNumber')} span={2}>
                <Text code>{selectedDelivery.trackingNumber}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.carrier')}>
                {selectedDelivery.carrier}
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.status')}>
                <Tag color={statusMap[selectedDelivery.status]?.color} icon={statusMap[selectedDelivery.status]?.icon}>
                  {statusMap[selectedDelivery.status]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.estimatedDelivery')}>
                {selectedDelivery.estimatedDeliveryDate ? formatTime(selectedDelivery.estimatedDeliveryDate) : t('logistics.detail.values.valueNotSet')}
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.actualDelivery')}>
                {selectedDelivery.actualDeliveryDate ? formatTime(selectedDelivery.actualDeliveryDate) : t('logistics.detail.values.notDelivered')}
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.pickupTime')}>
                {selectedDelivery.pickupDate ? formatTime(selectedDelivery.pickupDate) : t('logistics.detail.values.notPickedUp')}
              </Descriptions.Item>
              <Descriptions.Item label={t('logistics.detail.labels.notes')} span={2}>
                {selectedDelivery.notes || t('logistics.detail.values.none')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LogisticsTracking;
